import React, { useState, useEffect, useCallback } from 'react';
import { Tile, Guess } from '../types/game.ts';
import { isWordValid, getKeyboardState } from '../utils/wordle.ts';

interface WordleGameProps {
    targetWord: string;
    currentGuess: string;
    onGuessChange: (guess: string) => void;
    onGuessSubmit: () => void;
    guesses: Guess[];
    maxGuesses: number;
    isGameComplete: boolean;
    hasWon: boolean;
    disabled?: boolean;
}

const WordleGame: React.FC<WordleGameProps> = ({
    targetWord,
    currentGuess,
    onGuessChange,
    onGuessSubmit,
    guesses,
    maxGuesses,
    isGameComplete,
    hasWon,
    disabled = false
}) => {
    // Tüm React Hooks'ları en üste taşı
    const [keyboardState, setKeyboardState] = useState<Record<string, string>>({});

    useEffect(() => {
        if (!guesses || !Array.isArray(guesses)) return;
        const tiles = guesses.map(guess => guess.tiles).flat();
        setKeyboardState(getKeyboardState([tiles]));
    }, [guesses]);

    const handleKeyPress = useCallback(async (key: string) => {
        if (disabled || isGameComplete) return;

        if (key === 'ENTER') {
            if (currentGuess.length === 5 && await isWordValid(currentGuess)) {
                onGuessSubmit();
            }
        } else if (key === 'BACK' || key === 'BACKSPACE') {
            onGuessChange(currentGuess.slice(0, -1));
        } else if (key.length === 1 && currentGuess.length < 5) {
            onGuessChange(currentGuess + key.toUpperCase());
        }
    }, [currentGuess, disabled, isGameComplete, onGuessChange, onGuessSubmit]);

    useEffect(() => {
        const handleKeyDown = (event: KeyboardEvent) => {
            if (event.key === 'Enter') {
                handleKeyPress('ENTER');
            } else if (event.key === 'Backspace') {
                handleKeyPress('BACKSPACE');
            } else if (event.key.match(/[a-zA-ZçğıöşüÇĞIİÖŞÜ]/)) {
                handleKeyPress(event.key.toUpperCase());
            }
        };

        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [handleKeyPress]);

    // Güvenlik kontrolleri - tüm hooks'lardan sonra
    if (!targetWord || !guesses || !Array.isArray(guesses)) {
        return (
            <div className="max-w-md mx-auto text-center">
                <div className="text-white">Oyun yükleniyor...</div>
            </div>
        );
    }

    const renderTile = (tile: Tile, index: number) => (
        <div
            key={index}
            className={`wordle-tile ${tile.state} ${tile.state === 'empty' ? 'animate-pulse' : ''} ${tile.letter ? 'filled' : ''}`}
        >
            {tile.letter}
        </div>
    );

    const renderGuess = (guess: Guess, guessIndex: number) => (
        <div key={guessIndex} className="flex gap-1 sm:gap-1 mb-1">
            {guess.tiles.map((tile, tileIndex) => renderTile(tile, tileIndex))}
        </div>
    );

    const renderCurrentGuess = () => {
        const tiles: Tile[] = [];
        for (let i = 0; i < 5; i++) {
            if (i < currentGuess.length) {
                tiles.push({ letter: currentGuess[i], state: 'empty' });
            } else {
                tiles.push({ letter: '', state: 'empty' });
            }
        }
        return (
            <div className="flex gap-1 mb-1">
                {tiles.map((tile, index) => renderTile(tile, index))}
            </div>
        );
    };

    const renderEmptyGuess = () => (
        <div className="flex gap-1 sm:gap-1 mb-1">
            {Array.from({ length: 5 }, (_, index) => (
                <div key={index} className="wordle-tile empty" />
            ))}
        </div>
    );

    const keyboardRows = [
        ['Q', 'W', 'E', 'R', 'T', 'Y', 'U', 'I', 'O', 'P', 'Ğ', 'Ü', 'BACK'],
        ['A', 'S', 'D', 'F', 'G', 'H', 'J', 'K', 'L', 'Ş', 'İ'],
        ['Z', 'X', 'C', 'V', 'B', 'N', 'M', 'Ö', 'Ç', 'ENTER']
    ];

    return (
        <div className="max-w-md mx-auto px-4 sm:px-0">
            {/* Oyun tahtası */}
            <div className="mb-2 sm:mb-8">
                {guesses.map((guess, index) => renderGuess(guess, index))}
                {!isGameComplete && renderCurrentGuess()}
                {Array.from({ length: maxGuesses - guesses.length - (isGameComplete ? 0 : 1) }, (_, index) =>
                    renderEmptyGuess()
                )}
            </div>

            {/* Klavye */}
            <div className="space-y-1 sm:space-y-2">
                {keyboardRows.map((row, rowIndex) => (
                    <div key={rowIndex} className="flex justify-center gap-1 sm:gap-2">
                        {row.map((key) => (
                            <button
                                key={key}
                                onClick={() => handleKeyPress(key)}
                                disabled={disabled || isGameComplete}
                                className={`keyboard-key ${keyboardState[key] || ''} ${key === 'BACK' ? 'large' : ''
                                    } ${key === 'ENTER' ? 'enter' : ''
                                    } ${disabled || isGameComplete ? 'opacity-50 cursor-not-allowed' : ''}`}
                            >
                                {key === 'BACK' ? '⌫' : key}
                            </button>
                        ))}
                    </div>
                ))}
            </div>

            {/* Oyun durumu mesajları */}
            {isGameComplete && (
                <div className="mt-6 text-center">
                    {hasWon ? (
                        <p className="text-wordle-green text-xl font-semibold">
                            🎉 Tebrikler! Kelimeyi buldunuz!
                        </p>
                    ) : (
                        <p className="text-red-400 text-xl font-semibold">
                            😔 Maalesef bulamadınız. Kelime: <span className="font-bold">{targetWord}</span>
                        </p>
                    )}
                </div>
            )}
        </div>
    );
};

export default WordleGame;
